<script lang="ts">
  import { onDestroy } from 'svelte';
  import { getHafasResolve } from '../../lib/api';
  import { Autocomplete, type AutocompleteView } from '../../lib/autocomplete.svelte';
  import type { HafasStopPick } from '../../lib/hafas-stop';
  import type { Suggestion } from '../../lib/recents';
  import { toast } from '../../stores/toast.svelte';
  import type { HafasLocationJson } from '../../types/HafasLocationJson';
  import SuggestionsList from '../suggestions-list.svelte';

  interface Properties {
    /** Current text — parent controls it so URL restore can inject an initial value. */
    value: string;
    onInput: (next: string) => void;
    /** Called when the user picks a suggestion (click or Enter). */
    onPick: (stop: HafasStopPick, label: string) => void;
  }

  const { value, onInput, onPick }: Properties = $props();

  function toView(result: HafasLocationJson): AutocompleteView<HafasStopPick> {
    const coord = `${String(result.lat)}:${String(result.lon)}`;
    return {
      key: `stop:${result.ext_id}:${coord}`,
      label: result.name,
      badge: 'STOP',
      pick: { extId: result.ext_id, name: result.name, lat: result.lat, lon: result.lon },
      recent: { name: result.name, lat: result.lat, lon: result.lon, ext_id: result.ext_id },
    };
  }

  function recentToView(suggestion: Suggestion): AutocompleteView<HafasStopPick> | undefined {
    if (!suggestion.ext_id) return undefined;
    const coord = `${String(suggestion.lat)}:${String(suggestion.lon)}`;
    return {
      key: `recent:stop:${suggestion.ext_id}:${coord}`,
      label: suggestion.name,
      badge: 'RECENT',
      pick: {
        extId: suggestion.ext_id,
        name: suggestion.name,
        lat: suggestion.lat,
        lon: suggestion.lon,
      },
      recent: suggestion,
    };
  }

  const autocomplete = new Autocomplete<HafasLocationJson, HafasStopPick>({
    scope: () => 'dep',
    fetcher: async (query, signal) => {
      const response = await getHafasResolve(query, signal);
      return response.results;
    },
    toView,
    recentToView,
    toast,
    toastPrefix: 'Station lookup',
  });

  function handleInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    onInput(target.value);
    autocomplete.handleInput(target.value);
  }

  function onKeydown(event: KeyboardEvent): void {
    const picked = autocomplete.onKeydown(event);
    if (picked) {
      onInput(picked.label);
      onPick(picked.pick, picked.label);
    }
  }

  function pick(index: number): void {
    const picked = autocomplete.pick(index);
    onInput(picked.label);
    onPick(picked.pick, picked.label);
  }

  onDestroy(() => {
    autocomplete.dispose();
  });
</script>

<!-- HAFAS stop autocomplete. Hits `/v1/hafas-resolve` with debounce + abort;
     merges scope-`dep` recents; forwards keyboard nav to the dropdown. -->
<div class="stop-wrap">
  <label class="stop-label" for="dep-stop">Stop</label>
  <div class="stop-body">
    <input
      id="dep-stop"
      type="text"
      class="io-input"
      placeholder="Search a station"
      {value}
      autocomplete="off"
      oninput={handleInput}
      onfocus={() => {
        autocomplete.onFocus(value);
      }}
      onblur={() => {
        autocomplete.onBlur();
      }}
      onkeydown={onKeydown}
    />
    {#if autocomplete.open}
      <SuggestionsList
        items={autocomplete.items}
        activeIndex={autocomplete.activeIndex}
        emptyLabel="Start typing a station…"
        onSelect={pick}
        onHover={(index: number) => {
          autocomplete.setActive(index);
        }}
      />
    {/if}
  </div>
</div>

<style>
  .stop-wrap {
    margin-bottom: var(--space-sm);
  }

  .stop-label {
    display: block;
    font-size: var(--font-size-xs);
    color: var(--color-fg-muted);
    margin-bottom: 2px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .stop-body {
    position: relative;
  }
</style>
